/**
 * Customer Helper Utilities
 * Domain-specific helper functions for customer management
 *
 * Note: Generic formatters (phone, date, text) have been moved to utils/formatters.ts
 * This file now contains only customer-domain-specific logic.
 */

import type { ServiceStatus } from '../types/customer';

/**
 * Parse formatted phone number back to raw digits
 * Inverse of formatPhoneNumber from utils/formatters.ts
 */
export function parsePhoneNumber(formatted: string): string {
  return formatted.replace(/\D/g, '');
}

/**
 * Calculate service status based on next service due date
 * Customer-domain-specific business logic
 */
export function getServiceStatus(nextServiceDue?: string): ServiceStatus {
  if (!nextServiceDue) {
    return {
      status: 'none',
      color: 'default',
      label: 'No service scheduled',
    };
  }

  const dueDate = new Date(nextServiceDue);
  const today = new Date();
  const diffTime = dueDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return {
      status: 'overdue',
      color: 'error',
      label: `Overdue by ${Math.abs(diffDays)} days`,
      daysUntilDue: diffDays,
    };
  }

  if (diffDays <= 7) {
    return {
      status: 'due-soon',
      color: 'warning',
      label: `Due in ${diffDays} days`,
      daysUntilDue: diffDays,
    };
  }

  if (diffDays <= 30) {
    return {
      status: 'upcoming',
      color: 'success',
      label: `Due in ${diffDays} days`,
      daysUntilDue: diffDays,
    };
  }

  return {
    status: 'upcoming',
    color: 'success',
    label: `Due in ${diffDays} days`,
    daysUntilDue: diffDays,
  };
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
